import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseItem, useDatabaseData } from "@/components/page/hooks"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { zodResolver } from "@hookform/resolvers/zod"
import { Key } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

export const apikeys_crud = {
  title: "API Key",
  route: "/settings/apikeys/:id",
  component,
  resource: "apikeys",
  icon: Key,
  action: "read",
} satisfies PageConfig

const FormSchema = z.object({
  key: z.string(),
  type: z.string(),
  team_id: z.string(),
  description: z.string().optional(),
})

type FormData = z.infer<typeof FormSchema>

export function component() {

  const { data, loading, submit, toastSuccess, navigate, } = useDatabaseItem("api_keys")
  const { data: teams, loading: teamsLoading } = useDatabaseData("teams")

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data,
    values: data,
  })

  async function onSubmit(data: FormData) {
    console.log("Submit: ", data)
    await submit(data)
    toastSuccess()
    navigate("/settings/apikeys")
  }

  if (loading || teamsLoading) return <CRUDLoader />

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="api_keys" onSuccess={"/settings/apikeys"} />
          </PageTitle>

          <div className="rgrid">
            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="boxnet">Box.net</SelectItem>
                        <SelectItem value="databricks">Databricks</SelectItem>
                        <SelectItem value="deepseek">DeepSeek</SelectItem>
                        <SelectItem value="exa">Exa</SelectItem>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                        <SelectItem value="googleTranslate">Google Translate</SelectItem>
                        <SelectItem value="groq">Groq</SelectItem>
                        <SelectItem value="jina">Jina</SelectItem>
                        <SelectItem value="perplexity">Perplexity</SelectItem>
                        <SelectItem value="rescuenet">Rescue.net</SelectItem>
                        <SelectItem value="telerivet">Telerivet</SelectItem>
                        <SelectItem value="xai">XAI</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="zendesk">Zendesk</SelectItem>
                        <SelectItem value="codec">Codec</SelectItem>
                        <SelectItem value="whatsapp">Whatsapp Token</SelectItem>
                        <SelectItem value="whatsapp_phone">Whatsapp Phone id</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="team_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Team</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a team" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {teams?.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-12">
                <FormField control={form.control} name="key" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Key</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>
}
